import React, { useEffect, useState, useRef } from "react";

const currency = "USD";
const getInventoryURL = "https://herschelshapiro.com/checkout/getInventory";

const allCountryCodes = [
  "AF","AX","AL","DZ","AS","AD","AO","AI","AQ","AG","AR","AM","AW","AU","AT","AZ","BS","BH","BD","BB",
  "BY","BE","BZ","BJ","BM","BT","BO","BQ","BA","BW","BV","BR","IO","BN","BG","BF","BI","CV","KH","CM",
  "CA","KY","CF","TD","CL","CN","CX","CC","CO","KM","CG","CD","CK","CR","CI","HR","CU","CW","CY","CZ",
  "DK","DJ","DM","DO","EC","EG","SV","GQ","ER","EE","SZ","ET","FK","FO","FJ","FI","FR","GF","PF","TF",
  "GA","GM","GE","DE","GH","GI","GR","GL","GD","GP","GU","GT","GG","GN","GW","GY","HT","HM","VA","HN",
  "HK","HU","IS","IN","ID","IR","IQ","IE","IM","IL","IT","JM","JP","JE","JO","KZ","KE","KI","KP","KR",
  "KW","KG","LA","LV","LB","LS","LR","LY","LI","LT","LU","MO","MG","MW","MY","MV","ML","MT","MH","MQ",
  "MR","MU","YT","MX","FM","MD","MC","MN","ME","MS","MA","MZ","MM","NA","NR","NP","NL","NC","NZ","NI",
  "NE","NG","NU","NF","MK","MP","NO","OM","PK","PW","PS","PA","PG","PY","PE","PH","PN","PL","PT","PR",
  "QA","RE","RO","RU","RW","BL","SH","KN","LC","MF","PM","VC","WS","SM","ST","SA","SN","RS","SC","SL",
  "SG","SX","SK","SI","SB","SO","ZA","GS","SS","ES","LK","SD","SR","SJ","SE","CH","SY","TW","TJ","TZ",
  "TH","TL","TG","TK","TO","TT","TN","TR","TM","TC","TV","UG","UA","AE","GB","US","UM","UY","UZ","VU",
  "VE","VN","VG","VI","WF","EH","YE","ZM","ZW"
];

const countryNames = new Intl.DisplayNames(['en'], { type: 'region' });
const sortedCountryCodes = [...allCountryCodes].sort((a, b) =>
  countryNames.of(a).localeCompare(countryNames.of(b))
);

const PayPalBuyCallout = ({ title, slug, description, image_url, made_to_order = false, ships_in_days = 7 }) => {
  const isMadeToOrder = made_to_order === "true" || made_to_order === true;

  const [shippingRates, setShippingRates] = useState({});
  const [price, setPrice] = useState(0);
  const [country, setCountry] = useState("PH");
  const [date, setDate] = useState(Date.now());
  const [isAvailable, setIsAvailable] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(true);

  const contentRef = useRef(null);

  const addDays = (base, days) => {
    const d = new Date(base);
    d.setDate(d.getDate() + days);
    return d;
  };

  const formatDate = (d) =>
    d.toLocaleDateString("en-US", { month: "short", day: "numeric" });

  const shipDate = addDays(new Date(), Number(ships_in_days));
  const estArrivalStart = addDays(shipDate, 7);
  const estArrivalEnd = addDays(shipDate, 14);

  const shippingCost = shippingRates?.[country] ?? null;
  const canShip = shippingCost != null;
  const total = canShip ? Number(price) + shippingCost : null;

  // Prefilled contact link (always available)
  const countryHuman = countryNames.of(country) || country;
  const contactUrl = `/contact?reason=purchase-inquiry&sculpture=${encodeURIComponent(
    title || ""
  )}&country=${encodeURIComponent(countryHuman)}`;

  useEffect(() => {
    const fetchInventory = async () => {
      try {
        const res = await fetch(`${getInventoryURL}?slug=${slug}`);
        const data = await res.json();

        const item = data.inventory;
        const countryCode = data.country || "US";
        const serverDate = new Date((data?.date && !isNaN(Date.parse(data.date))) ? data.date : Date.now());

        if (!item) {
          setIsAvailable(false);
          setLoading(false);
          return;
        }

        setCountry(countryCode);
        setDate(serverDate);
        setIsAvailable(item.isAvailable && item.quantity > 0);
        setShippingRates(data.shippingRates || {});
        setPrice(item.price || 0);
      } catch (err) {
        console.error("Error loading inventory:", err);
        setIsAvailable(false);
      } finally {
        setLoading(false);
      }
    };

    fetchInventory();
  }, [slug]);

  useEffect(() => {
    if (!expanded) return;
    const wrapper = contentRef.current?.parentElement;
    if (!wrapper) return;
    requestAnimationFrame(() => {
      wrapper.style.maxHeight = `${contentRef.current.scrollHeight}px`;
    });
  }, [expanded, canShip, price, country]);

  // Always show content after loading; label changes with availability
  return (
    <div className={`paypal-buy-callout ${!isAvailable ? "soldout" : ""}`}>
      {/* Loading */}
      <div className={`paypal-loading ${loading ? "" : "is-hidden"}`}>
        <div className="paypal-loading-spinner" />
      </div>

      {/* Main content (don't hide when sold out) */}
      <div className={`paypal-content ${loading ? "is-hidden" : ""}`}>
        <div className="paypal-buy-header">
          <span className="paypal-buy-callout-label">
            <span className="label-icon" aria-hidden="true">{isAvailable ? "✅" : "🔴"}</span>
            <span className="label-text">{isAvailable ? "Available" : "Sold"}</span>
          </span>

          <button
            className={`paypal-buy-callout-button ${expanded ? "is-close" : ""}`}
            onClick={() => setExpanded(!expanded)}
            aria-label={expanded ? "Close Details" : "View Details"}
          >
            {expanded ? "✕" : "View Details"}
          </button>
        </div>

        <div
          className={`paypal-buy-ui-wrapper ${expanded ? "expanded" : ""}`}
          style={{
            maxHeight: expanded ? `${contentRef.current?.scrollHeight}px` : "0px",
            opacity: expanded ? 1 : 0,
          }}
        >
          <div className="paypal-buy-ui" ref={contentRef}>
            {/* Price + shipping details: only when available AND we can ship */}
            {isAvailable && (
              <>
                <div className="paypal-total">
                  <span className="paypal-total-label">Total:</span>
                  <span className="paypal-total-amount">
                    {currency} {canShip ? total.toLocaleString() : price.toLocaleString()}
                  </span>
                  <div className="paypal-total-subtext">
                    {canShip
                      ? `Price includes shipping to ${countryHuman}.`
                      : "Shipping not included — quotation required."}
                  </div>
                </div>

                <div className="paypal-duty-note">
                  <ul>
                    <li>
                      <span className="icon" aria-hidden="true">⏳</span>
                      <span>
                        {isMadeToOrder && <strong>Made to order. </strong>}
                        Ships within <strong>{ships_in_days} days</strong> from the Philippines
                      </span>
                    </li>
                    <li>
                      <span className="icon" aria-hidden="true">🚚</span>
                      <span>
                        Estimated Arrival: <strong>{formatDate(estArrivalStart)} – {formatDate(estArrivalEnd)}</strong>
                      </span>
                    </li>
                    <li>
                      <span className="icon">🌐</span>
                      <span>
                        {canShip
                          ? `Price includes shipping to ${countryHuman}.`
                          : "Shipping not included — quotation required."}
                      </span>
                    </li>
                  </ul>
                </div>
              </>
            )}

            {/* Country selection (only when available) */}
            {isAvailable && (
              <div className="paypal-buy-row">
                <label htmlFor="country">Ship to:</label>
                <select value={country} onChange={(e) => setCountry(e.target.value)}>
                  {sortedCountryCodes.map((code) => (
                    <option key={code} value={code}>
                      {countryNames.of(code)}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Always show the CTA at the very bottom */}
            <div className="inquiry-only-cta">
              <div className="inquiry-subtext">
                {isAvailable
                  ? "Reach out to me directly if you’d like to purchase this artwork."
                  : "This work is currently marked as sold. Please inquire if you’d like to discuss future availability."}
              </div>

              <a
                className="inquiry-button"
                href={contactUrl}
                aria-label="Purchase Inquiry"
              >
                Purchase Inquiry
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Keep your sold-out badge if you like the visual, but content now stays visible */}
      {/* <div className={`paypal-soldout ${!loading && !isAvailable ? "" : "is-hidden"}`}>
        <span className="paypal-soldout-label">Sold Out</span>
      </div> */}
    </div>
  );
};

export default PayPalBuyCallout;
